import { supabase } from "@/integrations/supabase/client";
import { BirdieGameGroup, Course, User, CurrentRound } from "@/types";

interface GamePlayerRow {
  player_id: string;
  profiles: { id: string; name: string | null; email: string | null; image_url: string | null };
}

// Get eligible games based on selected golfers and course (async, queries Supabase)
export const getEligibleGames = async (
  course: Course | null,
  golfers: User[]
): Promise<BirdieGameGroup[]> => {
  if (!course) return [];

  const golferIds = golfers.map(g => g.id);

  // Get all games at this course with their players
  const { data: games } = await supabase
    .from('games')
    .select('*, game_players(player_id, profiles:player_id(id, name, email, image_url, is_sample))')
    .eq('course_id', course.id);

  if (!games) return [];

  return games
    .filter(game => {
      // All game members must be in the selected golfers list
      const players = game.game_players as GamePlayerRow[];
      const memberIds = players.map(gp => gp.player_id);
      return memberIds.every((id: string) => golferIds.includes(id));
    })
    .filter(game => {
      // Filter by active date range
      if (game.start_date && game.end_date) {
        const now = new Date();
        return now >= new Date(game.start_date) && now <= new Date(game.end_date);
      }
      return true; // open-ended games are always active
    })
    .map(game => {
      const players = game.game_players as GamePlayerRow[];
      return {
        id: game.id,
        name: game.name,
        courseId: game.course_id,
        year: new Date(game.start_date).getUTCFullYear(),
        members: players.map(gp => ({
          id: gp.profiles.id,
          name: gp.profiles.name || 'Unknown',
          email: gp.profiles.email,
          imageUrl: gp.profiles.image_url,
        })),
        bet: game.bet || undefined,
      };
    });
};

// Create a new active round and store it in localStorage
export const createActiveRound = (
  golfers: User[],
  course: Course,
  eligibleGames: BirdieGameGroup[]
): CurrentRound => {
  const newRound: CurrentRound = {
    golfers,
    courseId: course.id,
    date: new Date().toISOString(),
    eligibleGames,
  };

  localStorage.setItem('activeRound', JSON.stringify(newRound));
  return newRound;
};

// Get game IDs that a specific golfer is part of
export const getGolferGameIds = (golferId: string, eligibleGames: BirdieGameGroup[]) => {
  return eligibleGames
    .filter(game => game.members.some(member => member.id === golferId))
    .map(game => game.id);
};

// Get games where at least one golfer has birdied a specific hole (async)
export const getGamesForHole = async (
  holeNumber: number,
  golfers: User[],
  eligibleGames: BirdieGameGroup[],
  courseId: string
): Promise<Map<string, string[]>> => {
  const gamesWithGolfers = new Map<string, string[]>();

  const golferIds = golfers.map(g => g.id);
  const { data: existingScores } = await supabase
    .from('scores')
    .select('user_id')
    .eq('course_id', courseId)
    .eq('hole_number', holeNumber)
    .in('user_id', golferIds);

  const birdiedPlayerIds = new Set(existingScores?.map(s => s.user_id) || []);

  for (const game of eligibleGames) {
    const golfersInGame = game.members
      .filter(m => birdiedPlayerIds.has(m.id))
      .map(m => m.id);

    if (golfersInGame.length > 0) {
      gamesWithGolfers.set(game.id, golfersInGame);
    }
  }

  return gamesWithGolfers;
};

// Calculate hole color based on birdie status across games (async)
export const calculateHoleColor = async (
  holeNumber: number,
  golfers: User[],
  eligibleGames: BirdieGameGroup[],
  courseId: string
): Promise<"none" | "some" | "all"> => {
  const gamesForHole = await getGamesForHole(holeNumber, golfers, eligibleGames, courseId);

  if (gamesForHole.size === 0) return "none";

  const allGolferIds = golfers.map(g => g.id);
  const allCovered = allGolferIds.every(id =>
    Array.from(gamesForHole.values()).some(playerIds => playerIds.includes(id))
  );

  return allCovered ? "all" : "some";
};
